import { useState, type ReactNode } from "react";
import { useStartDraftBoq } from "../../api/boq";
import { useCustomerOrder } from "../../api/customerOrders";
import { formatDate } from "../../api/dates";
import { useInvoice } from "../../api/invoices";
import { formatMoney } from "../../api/money";
import { useQuotation, useRaiseBoq, useRaiseSupplierOrder, type QuotationDetail } from "../../api/quotations";
import { StatusChip } from "../../components/ui";
import BoqWorkspace from "../boq/BoqWorkspace";
import { OrderView as CustomerOrderView, ReceiveForm } from "../customer-orders/CustomerOrdersPage";
import CheckActions from "../customer-orders/reader/CheckActions";
import CityOrderDrawer from "../customer-orders/reader/CityOrderDrawer";
import { InvoiceView } from "../invoices/InvoicesPage";
import OrderWorkspace from "../purchase-orders/OrderWorkspace";
import QuoteStep from "../quotations/QuoteStep";
import NextStepBanner from "./NextStepBanner";
import StepPipeline from "./StepPipeline";
import { STEPS, SUPPLIER_STEPS, factsOfDetail, jobRef, openingStep, stepsOf, type StepKey } from "./pipeline";
import { boqLabel, quotationLabel, quotationTone } from "./tones";

interface Props {
  id: number;
  /** The step the address names, or null to open where the next thing happens. */
  step: StepKey | null;
  /** Open the customer PO step with the City PO reader showing. */
  read: boolean;
  onStep: (step: StepKey, options?: { read?: boolean }) => void;
  office: boolean;
  supplierView: boolean;
}

/** One job: who and what it is, the one next step, the six documents as a
 * pipeline, and the chosen document under it.
 *
 * Each step renders the document's real workspace, not a link to one -- the
 * same component `/purchase-orders/:id` shows -- without a header of its own:
 * the job's header already says the number, the site, the status and the
 * money (Fix #10). A step whose document does not exist yet says so in the
 * lifecycle's own words, with the button that brings it into being when this
 * account may press it. */
export default function CommercialWorkspace({ id, step, read, onStep, office, supplierView }: Props) {
  const detail = useQuotation(id);

  if (detail.isPending) {
    return <p className="cm-job empty-state">Loading the job…</p>;
  }
  if (detail.isError) {
    return (
      <p className="cm-job notice bad" role="alert">
        {detail.error.status === 404 ? "There is no such job, or it is not yours to see." : detail.error.message}
      </p>
    );
  }
  const q = detail.data;
  const keys = supplierView ? SUPPLIER_STEPS : STEPS;
  const active = step !== null && keys.includes(step) ? step : openingStep(q, supplierView);
  const steps = stepsOf(factsOfDetail(q), keys);

  return (
    <article className="cm-job" aria-label={`Job ${jobRef(q.supplier, q.number)}`}>
      <JobHeader detail={q} />
      {supplierView ? null : <NextStepBanner detail={q} canAct={office} onStep={onStep} />}
      <StepPipeline steps={steps} active={active} onSelect={(next) => onStep(next)} />
      <section className="cm-doc" role="tabpanel" id="job-step-panel" aria-labelledby={`step-${active}`}>
        {active === "draft" ? <DraftStep detail={q} office={office} onStep={onStep} /> : null}
        {active === "quote" ? <QuoteStep detail={q} /> : null}
        {active === "po" ? <PurchaseOrderStep detail={q} office={office} /> : null}
        {active === "boq" ? <BoqStep detail={q} office={office} onStep={onStep} /> : null}
        {active === "customer" ? <CustomerOrderStep detail={q} office={office} read={read} /> : null}
        {active === "invoice" ? <InvoiceStep detail={q} /> : null}
      </section>
    </article>
  );
}

function JobHeader({ detail }: { detail: QuotationDetail }) {
  const site = detail.site?.name ?? detail.inspection?.chamber_name ?? "Site not linked";
  const who = detail.supplier ? `${detail.supplier.name} (${detail.supplier.acc_no})` : "Unknown supplier";
  const facts = [
    who,
    detail.inspection ? `Works order ${detail.inspection.work_order_no}` : null,
    detail.quote_date ? `Dated ${formatDate(detail.quote_date)}` : null,
  ].filter(Boolean);
  return (
    <header className="cm-job-head">
      <div className="cm-job-who">
        <div className="cm-job-title">
          <h1 className="mono">{jobRef(detail.supplier, detail.number)}</h1>
          <StatusChip tone={quotationTone(detail.status)}>{quotationLabel(detail.status)}</StatusChip>
          {detail.expired && detail.status !== "approved" ? <StatusChip tone="crit">Validity lapsed</StatusChip> : null}
          {detail.version > 1 ? <StatusChip>Version {detail.version}</StatusChip> : null}
        </div>
        <div className="cm-job-site">{site}</div>
        <div className="cm-job-facts">{facts.join(" · ")}</div>
      </div>
      <div className="cm-job-money">
        <div>
          <strong>{formatMoney(detail.foot.total_excl)}</strong> <span>excl VAT</span>
        </div>
        <div>
          {formatMoney(detail.foot.total_incl)} <span>incl VAT</span>
        </div>
      </div>
    </header>
  );
}

/** A step whose document does not exist yet: what it is waiting for, and the
 * one move that makes it, when there is one. */
function NotYet({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="cm-notyet">
      <h3>{title}</h3>
      <p>{children}</p>
      {action}
    </div>
  );
}

export function PurchaseOrderStep({ detail, office }: { detail: QuotationDetail; office: boolean }) {
  if (detail.supplier_order) {
    return <OrderWorkspace id={detail.supplier_order.id} />;
  }
  if (detail.status === "approved") {
    return (
      <NotYet title="No order yet" action={office ? <RaiseOrder detail={detail} /> : null}>
        Approved, but the order to {detail.supplier?.name ?? "the supplier"} was not raised
        {detail.expired
          ? `: the quoted prices were valid until ${detail.valid_until ? formatDate(detail.valid_until) : "a date that has passed"}. Say why they still hold and the order is raised with your reason on it.`
          : "."}
      </NotYet>
    );
  }
  return (
    <NotYet title="No order yet">An order is raised from an approved quotation. This one is {quotationLabel(detail.status).toLowerCase()}.</NotYet>
  );
}

/** Raise the order approval held back. On lapsed prices the reason is
 * required -- the server refuses without one -- and is kept on the order. */
function RaiseOrder({ detail }: { detail: QuotationDetail }) {
  const raise = useRaiseSupplierOrder(detail.id);
  const [reason, setReason] = useState("");
  const needsReason = detail.expired;
  return (
    <form
      className="cm-form cm-raise-order"
      onSubmit={(e) => {
        e.preventDefault();
        raise.mutate(reason);
      }}
    >
      {needsReason ? (
        <label className="field">
          Why do these prices still hold?
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="e.g. Macsteel confirmed by phone on 25 Sep that the prices stand"
            required
          />
        </label>
      ) : null}
      <div>
        <button type="submit" className="btn btn-primary" disabled={raise.isPending || (needsReason && reason.trim() === "")}>
          {raise.isPending ? "Raising the order…" : "Raise the supplier's order"}
        </button>
      </div>
      {raise.isError ? (
        <p className="notice bad" role="alert">
          {raise.error.message}
        </p>
      ) : null}
    </form>
  );
}

/** Step 1: the job priced by hand before the supplier quoted. It opens on the
 * bill screen, where it is filled like any other bill; it is never issued to
 * the City. A job without one can have one started here, under its works order. */
export function DraftStep({ detail, office, onStep }: { detail: QuotationDetail; office: boolean; onStep: Props["onStep"] }) {
  const start = useStartDraftBoq();
  if (detail.draft_boq) {
    return <BoqWorkspace id={detail.draft_boq.id} />;
  }
  const siteId = detail.site?.id ?? detail.inspection?.site_id ?? null;
  const workOrder = detail.inspection?.work_order_no ?? "";
  const canStart = office && workOrder.trim() !== "" && !detail.boq;
  return (
    <NotYet
      title="No draft BOQ"
      action={
        canStart && siteId !== null ? (
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={start.isPending}
              onClick={() => start.mutate({ site_id: siteId, work_order_no: workOrder }, { onSuccess: () => onStep("draft") })}
            >
              Start the draft BOQ
            </button>
            {start.isError ? <p className="notice bad">{start.error.message}</p> : null}
          </>
        ) : null
      }
    >
      The draft BOQ is the job priced by hand on the City's rate card before the supplier quotes. The customer BOQ is
      raised as a copy of it and amended for any additional work. Without one, the customer BOQ is built from the
      approved quotation.
    </NotYet>
  );
}

function BoqStep({ detail, office, onStep }: { detail: QuotationDetail; office: boolean; onStep: Props["onStep"] }) {
  const raise = useRaiseBoq(detail.id);
  if (detail.boq) {
    return <BoqWorkspace id={detail.boq.id} />;
  }
  if (detail.status !== "approved") {
    return <NotYet title="No bill yet">The customer BOQ is raised from an approved quotation.</NotYet>;
  }
  if (detail.boq_blocker) {
    return <NotYet title="The City's bill can't be raised yet">{detail.boq_blocker}</NotYet>;
  }
  return (
    <NotYet
      title="The City's bill hasn't been raised yet"
      action={
        office ? (
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={raise.isPending}
              onClick={() => raise.mutate(undefined, { onSuccess: () => onStep("boq") })}
            >
              Raise the customer BOQ
            </button>
            {raise.isError ? <p className="notice bad">{raise.error.message}</p> : null}
          </>
        ) : null
      }
    >
      {detail.draft_boq
        ? `It is raised as a copy of ${detail.draft_boq.name}, as you priced it. The approved quotation's full price is offered for K1 on it, and any additional work is added there.`
        : "B15, K1 and K2 are derived from the approved lines at list price; nothing is typed."}
    </NotYet>
  );
}

function CustomerOrderStep({ detail, office, read }: { detail: QuotationDetail; office: boolean; read: boolean }) {
  const order = useCustomerOrder(detail.customer_order?.id ?? null);
  if (!detail.boq) {
    return <NotYet title="No customer order yet">The City orders against a bill, and this job has none yet.</NotYet>;
  }
  if (!detail.customer_order) {
    return <CaptureOrder detail={detail} office={office} startReading={read} />;
  }
  if (order.isPending) {
    return <p className="empty-state">Loading the City's order…</p>;
  }
  if (order.isError) {
    return <p className="notice bad">{order.error.message}</p>;
  }
  return (
    <>
      <CheckActions orderId={order.data.id} number={order.data.orderNumber} status={order.data.status} hasPdf={order.data.documentUrl !== null} />
      <CustomerOrderView order={order.data} />
    </>
  );
}

/** No order from the City yet. "Read the City's PO" opens the reader over the
 * job, against its bill; "Capture by hand" opens the capture form here (for a
 * scanned PO), without sending the user away (Fix #2). `startReading` opens the
 * reader at once, as the next step's button asks; `startOpen` the hand form.
 * Exported for its test. */
export function CaptureOrder({
  detail,
  office,
  startOpen = false,
  startReading = false,
}: {
  detail: QuotationDetail;
  office: boolean;
  startOpen?: boolean;
  startReading?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const [reading, setReading] = useState(startReading && office);
  const boq = detail.boq;
  if (boq === null) {
    return null;
  }
  return (
    <div className="cm-notyet">
      <h3>No customer order against {boq.name} yet</h3>
      <p>Drop the City's PO to read it and check it against the bill. A scanned PO is captured by hand.</p>
      {office && !open ? (
        <div className="move-buttons">
          <button type="button" className="btn btn-primary" onClick={() => setReading(true)}>
            {"Read the City's PO"}
          </button>
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            Capture by hand
          </button>
        </div>
      ) : null}
      {office && open ? <ReceiveForm boqId={boq.id} onCancel={() => setOpen(false)} /> : null}
      {reading ? <CityOrderDrawer boqId={boq.id} orderId={null} onClose={() => setReading(false)} onSaved={() => setReading(false)} /> : null}
    </div>
  );
}

export function InvoiceStep({ detail }: { detail: QuotationDetail }) {
  const invoice = useInvoice(detail.invoice?.id ?? null);
  if (!detail.boq) {
    return <NotYet title="Not invoiced">An invoice follows the customer BOQ, which has not been raised yet.</NotYet>;
  }
  if (!detail.invoice) {
    return (
      <NotYet title="Not invoiced">
        {detail.boq.status === "approved"
          ? detail.customer_order?.status === "validated"
            ? "The City's PO is validated against the bill. The invoice is issued from the Customer BOQ step."
            : "The invoice is issued once the City's PO is read and validated against the bill."
          : `The invoice is issued from an approved bill; ${detail.boq.name} is ${boqLabel(detail.boq.status).toLowerCase()}.`}
      </NotYet>
    );
  }
  if (invoice.isPending) {
    return <p className="empty-state">Loading the invoice…</p>;
  }
  if (invoice.isError) {
    return <p className="notice bad">{invoice.error.message}</p>;
  }
  return <InvoiceView invoice={invoice.data} />;
}
